import { Box, Grid } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import WSTextField from '../TextBox/WSTextBox';
import { IsSmallScreen } from '../../commonFunctions/CommonFunctions';
import SearchIcon from '@mui/icons-material/Search';

interface IFilterSearchTextProps {
    rows: IRows;
    onFilterChange: (filters: IFilter[], search?: string) => void;
    alignment?: 'left' | 'center' | 'right';
    defaultFilter?: any[],
    searchMode?: 'field' | 'query';
    defaultSearch?: string;
}

export interface IRows {
    fieldName?: string;
}

interface IFilter {
    field: string;
    operator: Operator | string;
    value: string | number;
}

export enum Operator {
    EqualTo = 'eq',
    NotEqualTo = 'neq',
    GreaterThan = 'gt',
    LessThan = 'lt',
    Like = 'like',
}

export const FilterSearchText: React.FC<IFilterSearchTextProps> = React.memo(({ rows, onFilterChange, alignment = 'left', defaultFilter, searchMode = "field", defaultSearch = "" }) => {
    const methods = useForm({ defaultValues: { searchAll: defaultSearch } });
    const { register, handleSubmit, control, reset, watch } = methods;

    // Clearing the box should restore the full list without needing Enter.
    const searchText = watch('searchAll');
    const hadText = useRef(!!defaultSearch);
    useEffect(() => {
        const hasText = !!searchText && String(searchText).trim() !== '';
        if (hadText.current && !hasText) {
            onFilterChange(defaultFilter?.length ? [...defaultFilter] : []);
        }
        hadText.current = hasText;
    }, [searchText]);
    const isMobile = IsSmallScreen();

    const handleReset = () => {
        reset(); // ✅ Correct way to reset form
        onFilterChange([]); // Clear filters
    };

    const onSubmit = (data: Record<string, any>) => {

        const appliedFilters: any[] = [];

        if (defaultFilter?.length) {
            appliedFilters.push(...defaultFilter);
        }

        if (data.searchAll && data.searchAll !== "") {

            if (searchMode === 'query') {
                // ✅ send search separately
                onFilterChange(appliedFilters, data.searchAll);
                return;
            } else {
                // ✅ normal filter mode
                appliedFilters.push({
                    field: rows.fieldName,
                    operator: Operator.Like,
                    value: data.searchAll,
                });
            }
        }

        onFilterChange(appliedFilters);
    };


    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>

                <Grid container spacing={2} sx={{ margin: 1 }}>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            background: "#f3f3f3",
                            borderRadius: "14px",
                            px: 2,
                            height: 45,
                            width: "100%",
                            maxWidth: 320,
                            border: "1px solid #e0e0e0",
                        }}
                    >
                        {/* Input */}
                        <Box sx={{ flexGrow: 1 }}>
                            <WSTextField
                                control={control}
                                id="searchAll"
                                label=""
                                {...register("searchAll")}
                                name="searchAll"
                                placeholder="Search here"
                                variant="standard"
                                sx={{
                                    width: "100%",
                                    "& .MuiInputBase-root": {
                                        fontSize: "14px",
                                    },
                                    "& input": {
                                        padding: "10px 0",
                                    },
                                    "& .MuiInput-underline:before": {
                                        display: "none",
                                    },
                                    "& .MuiInput-underline:after": {
                                        display: "none",
                                    },
                                }}
                            />
                        </Box>

                        {/* Search Icon */}
                        <SearchIcon sx={{ color: "#000", cursor: "pointer" }} onClick={handleSubmit(onSubmit)} />
                    </Box>


                </Grid>

            </form>
        </FormProvider>
    );
});